/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {ErrorHandlerWrapper} from '../components/errorhandler/ErrorHandlerWrapper';

// defining the props definitions...
export interface WithErrorBoundaryProps {
  onErrorRecovery?: Function;
  omitRefresh?: boolean;
};

/**
 * The withErrorBoundary function is a higher-order component (HOC) used to wrap another
 * React component with error-handling functionality. It catches errors that occur within
 * the wrapped component and displays an error message or UI when an error is encountered.
 * Additionally, it provides the option to refresh or retry the operation when an error occurs.
 *
 * ```typescript
 *
 *  const Component = (props: Props) => (
 *    <div classes={styles.container}>
 *      <...>
 *    </div>
 *  );
 *
 *  // Wrap your component with error-handling functionality.
 *  const WrappedComponent = withErrorBoundary(Component);
 *
 *  // Use the WrappedComponent in your application.
 *  const App = () => {
 *    return (
 *      <div>
 *        <h1>My App</h1>
 *        <WrappedComponent />
 *      </div>
 *    );
 *  };
 *
 *  export default App;
 *
 * ```
 * @param Component The React component that want to wrap with error-handling functionality.
 * @param onRefresh The callback to be called when the user clicks on the refresh button.
 * @returns A new React component that wraps the provided Component with error-handling capabilities.
 */
export const withErrorBoundary = (Component: React.ComponentType, {
  onErrorRecovery,
  omitRefresh
}: WithErrorBoundaryProps = {}) => {
  return (props) => (
    <ErrorHandlerWrapper onRefresh={onErrorRecovery} omitRefresh={omitRefresh}>
      <Component {...props} />
    </ErrorHandlerWrapper>
  );
};

export default withErrorBoundary;
